import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ChangeRequestDetail, type ProjectRef } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';
import { AttachmentsField, useUploads } from '../components/Attachments';

/**
 * Route: `/change-requests/new` — a title, the details, and any files or voice
 * notes. Admins and team leaders review it; the client follows it here.
 */
export function ChangeRequestFormPage() {
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploads = useUploads();

  const [form, setForm] = useState({ title: '', details: '', projectId: '' });

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectRef[]>('/projects'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<ChangeRequestDetail>('/change-requests', {
        title: form.title.trim(),
        details: form.details.trim() || undefined,
        projectId: form.projectId || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: (request) => {
      toast.success(t('crs.created', { number: request.number }));
      void queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/change-requests/${request.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // The details, a file or a voice note — something has to say what to change.
  const saysSomething = form.details.trim() !== '' || uploads.fileIds.length > 0;

  return (
    <FormPage
      title={t('crs.new')}
      backTo="/change-requests"
      backLabel={t('crs.title')}
      submitLabel={t('common.send')}
      submitting={create.isPending}
      disabled={!form.title.trim() || !saysSomething || uploads.busy}
      onSubmit={() => create.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('crs.titleField')} required hint={t('crs.titleHint')}>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={300}
            autoFocus
          />
        </Field>

        <Field label={t('crs.details')} hint={t('crs.detailsHint')}>
          <Textarea
            value={form.details}
            onChange={(e) => setForm({ ...form, details: e.target.value })}
            onPaste={uploads.onPaste}
            className="min-h-[160px]"
          />
        </Field>

        <Field label={t('common.project')} hint={t('crs.projectHint')}>
          <Select value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
            <option value="">{t('common.noProject')}</option>
            {(projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {localized(p)}
              </option>
            ))}
          </Select>
        </Field>

        <AttachmentsField uploads={uploads} />

        {form.title.trim() !== '' && !saysSomething && (
          <p className="text-caption text-muted">{t('crs.needsSomething')}</p>
        )}
      </div>
    </FormPage>
  );
}
